import React, {
  createContext,
  useEffect,
  useMemo,
  useContext,
  useState,
  useRef,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import io from "socket.io-client";
import { useSnackbar } from "./SnackbarContext";
import { incrementUnreadNotificationsCount } from "../redux/actions/notificationActions";
import { showUpdateFeedButton } from "../redux/actions/linkupActions";
import {
  setFriendsList,
  updateFriendStatus,
} from "../redux/actions/socialActions";
import { getFriendsList } from "../api/usersAPI";
import {
  isNotificationAvailable,
  requestNotificationPermission,
} from "../utils/notificationUtils";
import { calculateDistance, calculateAge } from "../utils/utils";
import { customGenderOptions } from "../utils/customGenderOptions";
import { verifyCheckIn } from "../api/marketplaceAPI";

const SocketContext = createContext();

// Session storage keys for check-in tracking
const CHECKED_IN_KEY = "luul_checked_in_linkups";
const DISMISSED_KEY = "luul_dismissed_prompts";
const DISMISS_COOLDOWN_HOURS = 4; // Don't show again for 4 hours after dismissal

// Helper to get checked-in linkups from session storage
const getCheckedInLinkups = () => {
  try {
    const stored = sessionStorage.getItem(CHECKED_IN_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Helper to add a checked-in linkup
const addCheckedInLinkup = (requestId) => {
  const current = getCheckedInLinkups();
  if (!current.includes(requestId)) {
    current.push(requestId);
    sessionStorage.setItem(CHECKED_IN_KEY, JSON.stringify(current));
  }
};

// Helper to get dismissed prompts with timestamps
const getDismissedPrompts = () => {
  try {
    const stored = sessionStorage.getItem(DISMISSED_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

// Helper to add a dismissed prompt
const addDismissedPrompt = (requestId) => {
  const current = getDismissedPrompts();
  current[requestId] = Date.now();
  sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(current));
};

// Check if prompt was dismissed within cooldown period
const isPromptDismissed = (requestId) => {
  const dismissed = getDismissedPrompts();
  const dismissedAt = dismissed[requestId];
  if (!dismissedAt) return false;
  const hoursSinceDismissal = (Date.now() - dismissedAt) / (1000 * 60 * 60);
  return hoursSinceDismissal < DISMISS_COOLDOWN_HOURS;
};

export const SocketProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { addSnackbar } = useSnackbar();
  const [statusUpdates, setStatusUpdates] = useState([]);
  const [showStatusPanel, setShowStatusPanel] = useState(false);

  // Check-in prompt state
  const [checkInPrompt, setCheckInPrompt] = useState(null);
  // Track locally checked-in linkups for immediate UI updates
  const [localCheckedIn, setLocalCheckedIn] = useState(new Set());

  const user = useSelector((state) => state.loggedUser?.user);
  const userSettings = useSelector((state) => state.userSettings.userSettings);

  const userId = user?.id;
  const userName = user?.name;
  const userAvatar = user?.avatar;
  const userLat = user?.latitude;
  const userLng = user?.longitude;
  const userGender = user?.gender;

  const BASE_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5001";

  // Socket namespaces - defined as useMemo to maintain stable reference
  const NAMESPACES = useMemo(
    () => ({
      MANAGEMENT: "/linkup-management",
      REQUEST: "/linkup-request",
      FRIENDS: "/friends",
      NOTIFICATIONS: "/notifications",
    }),
    [],
  );

  // Use refs to store socket instances to prevent recreation
  const socketsRef = useRef({
    linkupManagement: null,
    linkupRequest: null,
    friend: null,
    notification: null,
  });

  // Initialize sockets once when userId is available
  useEffect(() => {
    if (!userId) {
      // Only disconnect if userId is explicitly cleared (not during initial load)
      // This prevents race conditions during mount
      return;
    }

    // Only create if not already created
    if (!socketsRef.current.linkupManagement) {
      const socketOptions = {
        query: { userId, userName, userAvatar },
        reconnection: true,
        reconnectionAttempts: 5,
        transports: ["websocket", "polling"],
      };

      socketsRef.current.linkupManagement = io(
        `${BASE_URL}${NAMESPACES.MANAGEMENT}`,
        socketOptions,
      );
      socketsRef.current.linkupRequest = io(
        `${BASE_URL}${NAMESPACES.REQUEST}`,
        socketOptions,
      );
      socketsRef.current.friend = io(
        `${BASE_URL}${NAMESPACES.FRIENDS}`,
        socketOptions,
      );
      socketsRef.current.notification = io(
        `${BASE_URL}${NAMESPACES.NOTIFICATIONS}`,
        socketOptions,
      );
    }

    // Use a flag to track component mount status
    let isMounted = true;

    return () => {
      isMounted = false;
      // Only cleanup on unmount, not on userId changes
      if (!isMounted) return;
    };
  }, [userId, BASE_URL, NAMESPACES]);

  // Send location updates for proximity detection
  useEffect(() => {
    const linkupRequestSocket = socketsRef.current.linkupRequest;
    if (!linkupRequestSocket || !userId || !userLat || !userLng) return;

    // Send initial location update
    linkupRequestSocket.emit("location-update", {
      user_id: userId,
      latitude: userLat,
      longitude: userLng,
    });

    // Set up interval to send location updates every 30 seconds
    const intervalId = setInterval(() => {
      linkupRequestSocket.emit("location-update", {
        user_id: userId,
        latitude: userLat,
        longitude: userLng,
      });
    }, 30000);

    return () => clearInterval(intervalId);
  }, [userId, userLat, userLng]);

  // Listen for service worker messages (e.g., TRIGGER_CHECK_IN from push notification actions)
  useEffect(() => {
    const handleServiceWorkerMessage = async (event) => {
      if (event.data?.type === "TRIGGER_CHECK_IN") {
        const { linkupRequestId, linkupId, userId: swUserId } = event.data;
        console.log("[SocketContext] TRIGGER_CHECK_IN from SW:", event.data);

        // Get fresh location for check-in
        const getLocation = () =>
          new Promise((resolve) => {
            if (navigator.geolocation) {
              navigator.geolocation.getCurrentPosition(
                (pos) =>
                  resolve({
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude,
                  }),
                () => resolve({ lat: userLat, lng: userLng }),
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
              );
            } else {
              resolve({ lat: userLat, lng: userLng });
            }
          });

        const location = await getLocation();

        // Verify check-in via API
        try {
          const result = await verifyCheckIn({
            linkup_request_id: linkupRequestId,
            linkup_id: linkupId,
            user_id: swUserId || userId,
            user_lat: location.lat,
            user_lng: location.lng,
          });

          if (result?.success) {
            addSnackbar("Check-in successful!", { timeout: 5000 });
          } else {
            addSnackbar(result?.message || "Check-in failed", {
              timeout: 5000,
            });
          }
        } catch (error) {
          console.error("[SocketContext] Check-in error:", error);
          addSnackbar("Check-in failed. Please try again.", { timeout: 5000 });
        }

        // Clear any existing check-in prompt
        setCheckInPrompt(null);
      }
    };

    navigator.serviceWorker?.addEventListener(
      "message",
      handleServiceWorkerMessage,
    );

    return () => {
      navigator.serviceWorker?.removeEventListener(
        "message",
        handleServiceWorkerMessage,
      );
    };
  }, [userId, userLat, userLng, addSnackbar]);

  // Set up event listeners
  useEffect(() => {
    const linkupManagementSocket = socketsRef.current.linkupManagement;
    const linkupRequestSocket = socketsRef.current.linkupRequest;
    const friendSocket = socketsRef.current.friend;
    const notificationSocket = socketsRef.current.notification;

    if (
      !linkupManagementSocket ||
      !linkupRequestSocket ||
      !friendSocket ||
      !notificationSocket
    )
      return;

    const refreshFriends = async () => {
      if (!userId) return;
      const res = await getFriendsList(userId);
      if (res?.success) dispatch(setFriendsList(res.friends));
    };

    const isLinkupWithinUserSettings = (linkup) => {
      const ageRange = userSettings?.ageRange || [18, 99];
      const distanceRange = userSettings?.distanceRange || [0, 50];
      const genderPreferences = userSettings?.genderPreferences || [
        "men",
        "women",
        ...customGenderOptions.map((g) => g.toLowerCase()),
      ];

      // Only apply distance filtering if user has valid coordinates
      if (
        userLat != null &&
        userLng != null &&
        linkup.latitude != null &&
        linkup.longitude != null
      ) {
        const distance = calculateDistance(
          userLat,
          userLng,
          linkup.latitude,
          linkup.longitude,
        );
        if (distance < distanceRange[0] || distance > distanceRange[1])
          return false;
      }

      const age = calculateAge(linkup.date_of_birth) || 0;
      if (age < ageRange[0] || age > ageRange[1]) return false;

      return !(
        genderPreferences.length > 0 &&
        !genderPreferences.includes(linkup.creator_gender?.toLowerCase())
      );
    };

    // Linkup Feed Logic
    const handleLinkupCreated = ({ linkup }) => {
      if (
        linkup.creator_id === userId ||
        !linkup.gender_preference.includes(userGender) ||
        !isLinkupWithinUserSettings(linkup)
      )
        return;
      dispatch(showUpdateFeedButton(true));
    };

    const handleLinkupUpdated = ({ linkup }) => {
      if (linkup.creator_id === userId || !isLinkupWithinUserSettings(linkup))
        return;
      dispatch(showUpdateFeedButton(true));
    };

    const handleLinkupDeleted = ({ linkup }) => {
      if (linkup.creator_id === userId || !isLinkupWithinUserSettings(linkup))
        return;
      dispatch(showUpdateFeedButton(true));
    };

    const handleLinkupClosed = ({ linkup }) => {
      // Skip if it's your own linkup
      if (linkup.creator_id === userId) return;

      // Check gender preference - handle both array format and PostgreSQL array string format
      const genderPref = linkup.gender_preference;
      if (genderPref && userGender) {
        let normalizedPrefs = [];

        if (Array.isArray(genderPref)) {
          // JavaScript array format
          normalizedPrefs = genderPref.map((g) => g.toLowerCase());
        } else if (typeof genderPref === "string") {
          // PostgreSQL array string format: {Men,Women,Agender,...}
          // Remove curly braces and split by comma, handling quoted values
          const cleaned = genderPref.replace(/^{|}$/g, "");
          normalizedPrefs = cleaned
            .split(",")
            .map((g) => g.replace(/^"|"$/g, "").toLowerCase().trim())
            .filter((g) => g.length > 0);
        }

        const normalizedUserGender = userGender.toLowerCase();

        if (
          normalizedPrefs.length > 0 &&
          !normalizedPrefs.includes(normalizedUserGender)
        ) {
          return; // User's gender not in preferences
        }
      }

      // Check if linkup matches user settings
      if (!isLinkupWithinUserSettings(linkup)) return;

      dispatch(showUpdateFeedButton(true));
    };

    // handleLinkupExpired was missing - reintroduced to ensure UI consistency
    const handleLinkupExpired = ({ message }) => {
      addSnackbar(message, { timeout: 7000 });
      dispatch(showUpdateFeedButton(true));
    };

    // Generic handler for linkup request events (new, accepted, declined)
    const handleLinkupRequestEvent = (data) => {
      const content = data.content || data.message || "New request update";
      addSnackbar(content, { timeout: 7000 });
      dispatch(incrementUnreadNotificationsCount());
    };

    const handleInAppNotification = (data) => {
      const message = data.body || data.content || "New activity";
      addSnackbar(message, { timeout: 6000 });
      dispatch(incrementUnreadNotificationsCount());
    };

    if (
      isNotificationAvailable() &&
      window.Notification.permission === "default"
    ) {
      requestNotificationPermission();
    }

    // --- Listeners: Management ---
    linkupManagementSocket.on("linkupCreated", handleLinkupCreated);
    linkupManagementSocket.on("linkupUpdated", handleLinkupUpdated);
    linkupManagementSocket.on("linkupDeleted", handleLinkupDeleted);
    linkupManagementSocket.on("linkupClosed", handleLinkupClosed);
    linkupManagementSocket.on("linkupExpired", handleLinkupExpired); // Re-added

    // --- Listeners: Request (These were missing) ---
    linkupRequestSocket.on("new-linkup-request", handleLinkupRequestEvent);
    linkupRequestSocket.on("request-accepted", handleLinkupRequestEvent);
    linkupRequestSocket.on("request-declined", handleLinkupRequestEvent);

    // --- Listener: Check-in Prompt (proximity-based) ---
    linkupRequestSocket.on("check-in-prompt", (data) => {
      console.log("[SocketContext] check-in-prompt received:", data);
      const requestId = data?.linkup_request_id;

      // Skip if already checked in
      if (
        requestId &&
        (getCheckedInLinkups().includes(requestId) ||
          localCheckedIn.has(requestId))
      ) {
        console.log(
          "[SocketContext] Skipping prompt - already checked in:",
          requestId,
        );
        return;
      }

      // Skip if dismissed recently
      if (requestId && isPromptDismissed(requestId)) {
        console.log(
          "[SocketContext] Skipping prompt - dismissed recently:",
          requestId,
        );
        return;
      }

      // Skip if linkup is not within check-in window (24 hours before to 24 hours after)
      if (data?.linkup_date) {
        const linkupDate = new Date(data.linkup_date);
        const now = new Date();
        const hoursUntilLinkup = (linkupDate - now) / (1000 * 60 * 60);

        if (hoursUntilLinkup > 24 || hoursUntilLinkup < -24) {
          console.log(
            "[SocketContext] Skipping prompt - outside check-in window:",
            hoursUntilLinkup.toFixed(1),
            "hours until linkup",
          );
          return;
        }
      }

      setCheckInPrompt(data);
    });

    // Note: Partner check-in notifications are handled by the notification service
    // via the in_app_notification event, so we don't need a separate listener here

    // --- Listeners: Notifications ---
    notificationSocket.on("in_app_notification", handleInAppNotification);

    // --- Listeners: Friends ---
    friendSocket.on("friend-request-accepted", () => {
      refreshFriends();
    });
    friendSocket.on("incoming-friend-request", refreshFriends);
    friendSocket.on("friend-list-updated", refreshFriends);
    friendSocket.on("friend-status-updated", (payload) => {
      const friendId = payload?.friendId ?? payload?.id;
      const status = payload?.status ?? payload?.public_status;
      const friendName = payload?.friendName;

      if (friendId && typeof status !== "undefined") {
        dispatch(updateFriendStatus(friendId, status));

        // Batch status updates for a better UX
        setStatusUpdates((prev) => {
          const updated = [...prev];
          const existingIndex = updated.findIndex(
            (u) => u.friendId === friendId,
          );
          const entry = { friendId, status, friendName, ts: Date.now() };
          if (existingIndex >= 0) {
            updated[existingIndex] = entry;
          } else {
            updated.push(entry);
          }

          // Keep most recent 20 updates to avoid list growth
          const max = 20;
          if (updated.length > max) {
            // sort by timestamp and keep newest
            updated.sort((a, b) => a.ts - b.ts);
            return updated.slice(updated.length - max);
          }

          return updated;
        });

        // Show panel and let user dismiss manually to avoid spammy auto-hides
        setShowStatusPanel(true);
      }
    });

    return () => {
      linkupManagementSocket.off("linkupCreated");
      linkupManagementSocket.off("linkupUpdated");
      linkupManagementSocket.off("linkupDeleted");
      linkupManagementSocket.off("linkupClosed");
      linkupManagementSocket.off("linkupExpired");

      linkupRequestSocket.off("new-linkup-request");
      linkupRequestSocket.off("request-accepted");
      linkupRequestSocket.off("request-declined");
      linkupRequestSocket.off("check-in-prompt");

      notificationSocket.off("in_app_notification");

      friendSocket.off("friend-request-accepted");
      friendSocket.off("incoming-friend-request");
      friendSocket.off("friend-list-updated");
      friendSocket.off("friend-status-updated");
    };
  }, [
    userId,
    userLat,
    userLng,
    userGender,
    userSettings,
    addSnackbar,
    dispatch,
  ]);

  // Function to send location update for proximity detection
  const sendLocationUpdate = (latitude, longitude) => {
    const linkupRequestSocket = socketsRef.current.linkupRequest;
    if (linkupRequestSocket && userId) {
      linkupRequestSocket.emit("location-update", {
        user_id: userId,
        latitude,
        longitude,
      });
    }
  };

  // Dismiss check-in prompt (user clicked "not now")
  const dismissCheckInPrompt = () => {
    if (checkInPrompt?.linkup_request_id) {
      addDismissedPrompt(checkInPrompt.linkup_request_id);
    }
    setCheckInPrompt(null);
  };

  // Mark a linkup as checked in (called after successful check-in)
  const markAsCheckedIn = (requestId) => {
    if (requestId) {
      addCheckedInLinkup(requestId);
      setLocalCheckedIn((prev) => new Set([...prev, requestId]));
      setCheckInPrompt(null);
    }
  };

  // Emit check-in success to partner
  const notifyCheckInComplete = (data) => {
    const linkupRequestSocket = socketsRef.current.linkupRequest;
    if (linkupRequestSocket) {
      linkupRequestSocket.emit("user-checked-in", data);
    }
  };

  const value = useMemo(
    () => ({
      linkupManagementSocket: socketsRef.current.linkupManagement,
      linkupRequestSocket: socketsRef.current.linkupRequest,
      friendSocket: socketsRef.current.friend,
      notificationSocket: socketsRef.current.notification,
      statusUpdates,
      showStatusPanel,
      setShowStatusPanel,
      // Check-in prompt
      checkInPrompt,
      dismissCheckInPrompt,
      markAsCheckedIn,
      notifyCheckInComplete,
      localCheckedIn,
      sendLocationUpdate,
    }),
    [statusUpdates, showStatusPanel, checkInPrompt, localCheckedIn, userId],
  );

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};

export const useSockets = () => {
  const context = useContext(SocketContext);
  if (!context)
    throw new Error("useSockets must be used within a SocketProvider");
  return context;
};
